import { Component, Inject, ViewChild, ElementRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Firestore, doc, updateDoc, setDoc } from '@angular/fire/firestore';
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
} from '@angular/fire/storage';
import { UxserviceService } from 'src/app/shared/services/uxservice.service';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { getDoc } from '@angular/fire/firestore';
import { RealtimeBookingService } from 'src/app/shared/services/realtime-booking.service';

@Component({
  selector: 'app-upload-lpo-dialog',
  templateUrl: './upload-lpo-dialog.component.html',
  styleUrls: ['./upload-lpo-dialog.component.scss'],
})
export class UploadLpoDialogComponent {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  selectedFile: File | null = null;
  previewUrl: string | null = null;
  isUploading = false;
  uploadProgress = 0;

  constructor(
    private afs: Firestore,
    private ux: UxserviceService,
    private notificationService: NotificationService,
    private dialogRef: MatDialogRef<UploadLpoDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: { bookingId: string; lpoUrl?: string },
    private rtBooking: RealtimeBookingService, // Inject RTDB Service
  ) {
    if (data.lpoUrl) {
      this.previewUrl = data.lpoUrl;
    }
  }

  openFilePicker() {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (!file) return;

    if (!(file.type.includes('pdf') || file.type.includes('image'))) {
      this.ux.showError('Please select a PDF or image file.');
      return;
    }

    this.selectedFile = file;
    this.previewUrl = URL.createObjectURL(file);
  }

  async uploadLPO() {
    if (!this.selectedFile) {
      this.ux.showError('Please select a file to upload.');
      return;
    }

    this.isUploading = true;
    try {
      const storage = getStorage();
      const filePath = `LPOs/${this.data.bookingId}/${Date.now()}_${this.selectedFile.name}`;
      const fileRef = ref(storage, filePath);
      const uploadResult = await uploadBytes(fileRef, this.selectedFile);
      const downloadURL = await getDownloadURL(uploadResult.ref);

      const pdfDocRef = doc(this.afs, `pdfcollection/${this.data.bookingId}`);
      const pdfSnap = await getDoc(pdfDocRef);
      const existingPdfData = pdfSnap.exists() ? pdfSnap.data() as any : null;

      const bookingRef = doc(this.afs, 'booking-list', this.data.bookingId);
      const snapshot = await getDoc(bookingRef);
      const bookingData = snapshot.exists() ? snapshot.data() as any : null;
      const qNum = existingPdfData?.quotationNumber ?? (bookingData?.quotationNumber ?? null);

      // Save URL to pdfcollection only, not to booking-list
      await setDoc(
        pdfDocRef,
        {
          lpo: {
            pdflink: downloadURL,
            uploadedAt: new Date(),
            label: 'Uploaded LPO',
          },
          bookingId: this.data.bookingId,
          quotationNumber: qNum,
        },
        { merge: true },
      );

      // 🔥 Sync with Realtime DB so Vendor sees it immediately
      await this.rtBooking.updateLpo(this.data.bookingId, { url: downloadURL });

      // Notify Vendor
      if (snapshot.exists()) {
        
        // 🚀 NEW: Transition Confirmed to Started if LPO is now present
        const currentStatus = bookingData['status'] || 'Pending';
        if (currentStatus.toLowerCase() === 'confirmed') {
          await updateDoc(bookingRef, { status: 'Started' });
          await this.rtBooking.updateStatus(this.data.bookingId, 'Started');
        }

        const vendorId = bookingData['equipment']?.bywhom;
        if (vendorId) {
          const notification = {
            title: 'LPO Uploaded',
            message: `Customer has uploaded an LPO for Booking #${this.data.bookingId}`,
            type: 'success' as const,
            link: `/business/booking/booking-details/${this.data.bookingId}`,
            bookingId: this.data.bookingId,
          };

          this.notificationService.sendNotification(vendorId, notification);
        }
      }

      this.previewUrl = downloadURL;
      this.dialogRef.close({ uploadedUrl: downloadURL });
    } catch (err) {
      console.error('❌ Upload error:', err);
      this.ux.showError('Failed to upload LPO.');
    } finally {
      this.isUploading = false;
    }
  }

  close() {
    this.dialogRef.close();
  }
}
